import type { ArsColorCode, BarDiagramData, PhaseSlot, GroupRow, PlanInfo, RawTxtData } from '../types/barDiagram';

/**
 * Parsea un fichero .TXT generado por ARS_93 con los datos de configuración de un cruce.
 *
 * El TXT usa "estados lógicos" que se mapean a p-codes:
 *   - Estados ≥4 son p-codes directos (6→p6, 7→p7, 8→p8, 10→p10, 11→p11, etc.)
 *   - Estado 1 = verde (p1), siempre. Si fuera peatonal, ARS_93 usa estado 9 (→p9)
 *   - Estado 2 = transición: primer 2 tras un 1 → p2 (ámbar), siguientes → p3 (rojo)
 *   - Estado 3 = rojo (p3)
 *   - Estado 0 = apagado (p0)
 */
export function parseArsTxt(text: string): BarDiagramData {
  const lines = new Map<string, string>();
  for (const line of text.split('\n')) {
    const idx = line.indexOf(':');
    if (idx > 0) {
      lines.set(line.slice(0, idx).trim(), line.slice(idx + 1).trim());
    }
  }

  // --- Campos básicos ---
  const filename = lines.get('F') ?? '';
  const cruceMatch = filename.match(/(\d{2,4})/);
  const cruilla = cruceMatch?.[1] ?? '';

  const numGrupos = parseInt(lines.get('NumGrupos') ?? '16', 10);

  // --- Desfases ---
  const desf = parseNumberList(lines.get('Desf') ?? '');

  // --- Planes: P: [35, 13], [45, 18], ... ---
  const planArrays = parseArrayOfArrays(lines.get('P') ?? '');

  // --- Tipos de slot: F, t, t, F, t, t ---
  const tipos = (lines.get('TablaFasesTransTipo') ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  // --- Duraciones de transiciones ---
  const transTime = parseNumberList(lines.get('TablaFasesTransTime') ?? '');

  // --- Colores por slot: [1, 3, 6, 3], [2, 3, 7, 3], ... ---
  const colorsPerSlot = parseArrayOfArrays(lines.get('TablaFasesTrans') ?? '');

  // --- Construir PhaseSlots ---
  const numSlots = tipos.length;
  const phases: PhaseSlot[] = [];
  let phaseCounter = 0;

  // T(seg) para cada slot: fases F usan P[0], transiciones t usan TablaFasesTransTime
  const defaultPlan = planArrays[0] ?? [];
  let faseIdx = 0;

  for (let s = 0; s < numSlots; s++) {
    const tipo = tipos[s];
    const isPhase = tipo === 'F';
    let duration: number;

    if (isPhase) {
      duration = defaultPlan[faseIdx] ?? 0;
      faseIdx++;
      phaseCounter++;
    } else {
      duration = transTime[s] ?? 0;
    }

    phases.push({
      label: isPhase ? `F${phaseCounter}` : tipo + (s > 0 ? '' : ''),
      duration,
      type: isPhase ? 'phase' : 'transition',
    });
  }

  // Reconstruir labels t numerados
  let tCount = 0;
  for (let s = 0; s < phases.length; s++) {
    if (phases[s].type === 'phase') {
      tCount = 0;
    } else {
      tCount++;
      phases[s].label = `t${tCount}`;
    }
  }

  // --- Extraer estados brutos por grupo y mapear a p-codes ---
  const groups: GroupRow[] = [];
  for (let g = 0; g < numGrupos; g++) {
    const states: number[] = [];
    for (let s = 0; s < numSlots; s++) {
      const slotColors = colorsPerSlot[s] ?? [];
      states.push(g < slotColors.length ? slotColors[g] : 3);
    }

    // Filtrar grupos con estado constante en todos los slots (ej: siempre 8 = ámbar intermitente)
    if (states.every(v => v === states[0])) continue;

    const cells: ArsColorCode[] = [];
    mapStateColors(states, cells);
    groups.push({ id: g + 1, label: `G${g + 1}`, cells });
  }

  // --- Calcular tiempo total de transiciones (fijo, independiente del plan) ---
  let totalTransTime = 0;
  for (let s = 0; s < numSlots; s++) {
    if (tipos[s] !== 'F') totalTransTime += transTime[s] ?? 0;
  }

  // --- Construir PlanInfo ---
  const plans: PlanInfo[] = [];
  const numPlans = Math.max(desf.length, planArrays.length);
  for (let p = 0; p < numPlans; p++) {
    const phaseDurations: (number | null)[] = [];
    const planDurs = planArrays[p] ?? [];
    let fi = 0;
    for (let s = 0; s < numSlots; s++) {
      if (tipos[s] === 'F') {
        phaseDurations.push(planDurs[fi] ?? null);
        fi++;
      } else {
        phaseDurations.push(null);
      }
    }

    // Desfase mostrado = (Desf - F1_duración) mod CicloTotal
    // El TXT almacena el desfase al inicio de F1; el diagrama lo muestra al final de F1
    const rawOffset = desf[p] ?? 0;
    const f1Duration = planDurs[0] ?? 0;
    const cycleLength = planDurs.reduce((a, b) => a + b, 0) + totalTransTime;
    const displayOffset = cycleLength > 0
      ? ((rawOffset - f1Duration) % cycleLength + cycleLength) % cycleLength
      : rawOffset;

    plans.push({
      offset: displayOffset,
      rawOffset,
      phaseDurations,
    });
  }

  // --- Metadata ---
  const metadata = {
    cruilla,
    localitat: '',
    projecte: 'ARS_93',
    data: extractDateFromFilename(filename),
    regulador: '',
    planol: '',
    modificat: '',
  };

  // --- Datos brutos para reconstrucción (serialización TXT) ---
  const rawTxt: RawTxtData = {
    filename,
    numGrupos,
    numFases: parseInt(lines.get('NumFases') ?? '2', 10),
    fullStateMatrix: colorsPerSlot,
    desf,
    desfS0: parseNumberList(lines.get('Desf_S0') ?? ''),
    planArrays,
    tipos,
    transTimeRaw: transTime,
    incRaw: lines.get('INC') ?? '',
    transicionesTRaw: lines.get('TablaTransicionesT') ?? '',
    transicionesCRaw: lines.get('TablaTransicionesC') ?? '',
  };

  return { metadata, phases, groups, plans, rawTxt };
}

// --- Mapeo de colores ---

/** Estados del TXT que son p-codes directos (≥4) */
const DIRECT_PCODE_STATES = new Set([4, 5, 6, 7, 8, 9, 10, 11, 14, 15, 16, 22, 39]);

/**
 * Mapea estados del TXT a p-codes ARS_93.
 *
 * El TXT ya trae los estados pre-codificados para todos los tipos de grupo:
 * - Vehiculares: 1 (verde), 2 (transición), 3 (rojo)
 * - Peatonales: usarían 9 directamente (p9 = verde intermitente lento)
 * - Companions: usan 6, 7, 11 directamente (p6, p7, p11)
 * - Especiales: 4, 5, 8, 10, 14, 15, 16, 22, 39 → p-codes directos
 *
 * El único estado con contexto es el 2: el primer 2 tras un 1 → p2 (ámbar),
 * los siguientes 2 → p3 (rojo).
 */
function mapStateColors(states: number[], cells: ArsColorCode[]): void {
  let afterActive = false;
  let firstTransitionDone = false;

  for (const state of states) {
    let pcode: ArsColorCode;

    if (DIRECT_PCODE_STATES.has(state)) {
      // P-codes directos: el número del estado ES el p-code
      pcode = `p${state}` as ArsColorCode;
      afterActive = false;
      firstTransitionDone = false;
    } else {
      switch (state) {
        case 1:
          pcode = 'p1';
          afterActive = true;
          firstTransitionDone = false;
          break;
        case 2:
          if (afterActive && !firstTransitionDone) {
            pcode = 'p2'; // ámbar (primera transición tras verde)
            firstTransitionDone = true;
          } else {
            pcode = 'p3'; // rojo (transiciones siguientes)
          }
          afterActive = false;
          break;
        case 0:
          pcode = 'p0';
          afterActive = false;
          firstTransitionDone = false;
          break;
        default:
          pcode = 'p3';
          afterActive = false;
          firstTransitionDone = false;
          break;
      }
    }

    cells.push(pcode);
  }
}

// --- Helpers de parseo ---

/** Parsea "56, 46, 38, 83" → [56, 46, 38, 83] */
function parseNumberList(s: string): number[] {
  return s.split(',').map(v => parseInt(v.trim(), 10)).filter(n => !isNaN(n));
}

/** Parsea "[35, 13], [45, 18], ..." → [[35,13], [45,18], ...] */
function parseArrayOfArrays(s: string): number[][] {
  const result: number[][] = [];
  const regex = /\[([^\]]*)\]/g;
  let match;
  while ((match = regex.exec(s)) !== null) {
    const inner = match[1].trim();
    if (inner === '') {
      result.push([]);
    } else {
      result.push(inner.split(',').map(v => parseInt(v.trim(), 10)).filter(n => !isNaN(n)));
    }
  }
  return result;
}

/** Extrae fecha del nombre de fichero: "028-20250206.TXT" → "2025-02-06" */
function extractDateFromFilename(filename: string): string {
  const match = filename.match(/(\d{4})(\d{2})(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return '';
}
